import { ArrowRight, LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import GoogleTranslate from '@/components/GoogleTranslate';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Public marketing-site header.
 *
 * Two-row CSS grid:
 *   Row 1 → centered full-color wordmark (1SimpleCycler 3.0)
 *   Row 2 → full (uncompressed) Google Translate widget + single CTA
 *           (Login when anonymous, Dashboard when authed).
 *
 * The JOIN / Sign-Up button is intentionally absent from the header — new-
 * visitor conversion is handled by the Hero + FinalCTA blocks lower on the
 * page so the global header stays uncluttered and brand-forward.
 *
 * Note: this header is rendered only on the public Home page. The member
 * SPA (`MemberApp`) and admin SPA (`AdminApp`) ship their own shells.
 */
const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const { authUser, bootLoading } = useAuth();
  const isAuthed = !bootLoading && !!authUser;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-[rgba(8,8,9,0.85)] border-b border-[rgba(255,182,18,0.14)]' : 'bg-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="sc-public-header__grid max-w-7xl mx-auto">
        {/* Row 1 — centered wordmark logo */}
        <Link to="/" className="sc-public-header__brand" data-testid="brand-link" aria-label="1SimpleCycler 3.0 — home">
          <img src="/brand/wordmark-1sc.png" alt="1SimpleCycler 3.0" className="sc-public-header__wordmark" data-testid="brand-wordmark" />
        </Link>

        {/* Row 2 — full Google Translate + single CTA */}
        <div className="sc-public-header__controls">
          <GoogleTranslate variant="full" />
          {isAuthed ? (
            <Link to="/dashboard" className="sc-btn-gold sc-btn-compact" data-testid="header-cta-dashboard">
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
              <span>Dashboard</span>
            </Link>
          ) : (
            <Link to="/login" className="sc-btn-gold sc-btn-compact" data-testid="header-cta-login">
              <LogIn className="w-3.5 h-3.5" strokeWidth={2.6} />
              <span>Login</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default SiteHeader;
